import React, { Suspense } from 'react';
import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { OfflineProvider } from './contexts/OfflineContext';
// ThemeProvider kaldırıldı - basit çözüm için
import Logger from './utils/logger';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PerformanceDashboard from './components/PerformanceDashboard';
import AccessibilityToolbar from './components/AccessibilityToolbar';
import Login from './pages/Login';

// Public (kimlik dogrulamasiz) SaaS ekranlari: kayit, sifre sifirlama, davet.
const Kayit = lazy(() => import('./pages/Kayit'));
const SifreSifirla = lazy(() => import('./pages/SifreSifirla'));
const DavetKabul = lazy(() => import('./pages/DavetKabul'));

// Lazy loading for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerNew = lazy(() => import('./pages/CustomerNew'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const Elevators = lazy(() => import('./pages/Elevators'));
const ElevatorNew = lazy(() => import('./pages/ElevatorNew'));
const ElevatorDetail = lazy(() => import('./pages/ElevatorDetail'));
const WorkOrders = lazy(() => import('./pages/WorkOrders'));
const WorkOrderNew = lazy(() => import('./pages/WorkOrderNew'));
const WorkOrderDetail = lazy(() => import('./pages/WorkOrderDetail'));
const WorkOrderView = lazy(() => import('./pages/WorkOrderView'));
const InventoryNew = lazy(() => import('./pages/InventoryNew'));
const InventoryDetail = lazy(() => import('./pages/InventoryDetail'));
const Invoices = lazy(() => import('./pages/Invoices'));
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail'));
const Users = lazy(() => import('./pages/Users'));
const Permissions = lazy(() => import('./pages/Permissions'));
const Profile = lazy(() => import('./pages/Profile'));
const Chat = lazy(() => import('./pages/Chat'));
const QRScanner = lazy(() => import('./pages/QRScanner'));
const ErrorLogs = lazy(() => import('./pages/ErrorLogs'));
const UserSettings = lazy(() => import('./pages/UserSettings'));
const ElevatorPriceQuote = lazy(() => import('./pages/ElevatorPriceQuote'));
const PersonnelManagement = lazy(() => import('./pages/PersonnelManagement'));
const SupervisorPlanning = lazy(() => import('./pages/SupervisorPlanning'));
const DomainOperations = lazy(() => import('./pages/DomainOperations'));
const IntegrityChain = lazy(() => import('./pages/IntegrityChain'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Uyum = lazy(() => import('./pages/Uyum'));
const BakimOperasyon = lazy(() => import('./pages/BakimOperasyon'));
const Ticari = lazy(() => import('./pages/Ticari'));
const Portal = lazy(() => import('./pages/Portal'));
const KurulumAktarimi = lazy(() => import('./pages/KurulumAktarimi'));
const Parametreler = lazy(() => import('./pages/Parametreler'));
const SahaEkrani = lazy(() => import('./pages/SahaEkrani'));
const KvkkYonetim = lazy(() => import('./pages/KvkkYonetim'));

// Birlestirilmis ekranlar: menudeki cakismalari kaldirmak icin mevcut
// sayfalar sekme icine tasindi (bkz. components/ds/SekmeliSayfa).
const InstallationManagement = lazy(() => import('./pages/InstallationManagement'));
const Montaj = lazy(() => import('./pages/Montaj'));
const Uyarilar = lazy(() => import('./pages/Uyarilar'));
const Entegrasyonlar = lazy(() => import('./pages/Entegrasyonlar'));
const Envanter = lazy(() => import('./pages/Envanter'));
const YapayZeka = lazy(() => import('./pages/YapayZeka'));
const ForcePasswordChange = lazy(() => import('./pages/ForcePasswordChange'));

// AI Components


// Protected Route bileşeni
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

/**
 * Oturum durumundan BAGIMSIZ calismasi gereken yollar.
 *
 * Bu iki sayfa e-postadaki baglantiyla aciliyor. Asagidaki erken donusler
 * (zorunlu sifre degistirme, bina yoneticisi portali) tum rotalari yuttugu
 * icin, baska bir hesapla oturumu acik olan kullanici sifre sifirlama veya
 * davet baglantisina tikladiginda ilgisiz bir ekrana atiliyordu.
 */
const OTURUMDAN_BAGIMSIZ_YOLLAR = ['/sifre-sifirla', '/davet/kabul'];

function oturumdanBagimsizYolMu() {
  if (typeof window === 'undefined') return false;
  const yol = window.location.pathname;
  return OTURUMDAN_BAGIMSIZ_YOLLAR.some((p) => yol === p || yol.startsWith(p + '/'));
}

function App() {
  const { user, mustChangePassword } = useAuth();

  Logger.info('App component started, user:', user ? 'logged in' : 'not logged in');

  const bagimsizYol = oturumdanBagimsizYolMu();

  // Kimlik bilgileri sizan hesaplar: sifre yenilenene kadar uygulamanin
  // hicbir yerine erisim yok. Sunucu tarafinda da zorlaniyor
  // (middleware/passwordReset.js); burasi kullaniciya net bir yol gosteriyor.
  if (user && mustChangePassword && !bagimsizYol) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <ForcePasswordChange />
      </Suspense>
    );
  }

  // Bina yoneticisi sirket arayuzunu hic gormuyor: kendi portali disinda
  // gidecegi bir yer yok. Sunucu tarafinda da kapali (middleware/auth.js);
  // burasi dogru ekrani gosteriyor.
  if (user && user.role === 'bina_yoneticisi' && !bagimsizYol) {
    return (
      <OfflineProvider>
        <div className="App">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/portal" element={<Portal />} />
              <Route path="*" element={<Navigate to="/portal" replace />} />
            </Routes>
          </Suspense>
        </div>
      </OfflineProvider>
    );
  }

  return (
    <OfflineProvider>
        <div className="App">
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={
          user ? <Navigate to="/dashboard" replace /> : <Login />
        } />

        {/* Self servis firma kaydi — girisi olan kullanicinin isi yok. */}
        <Route path="/kayit" element={
          user ? <Navigate to="/dashboard" replace /> : (
            <Suspense fallback={<LoadingSpinner />}>
              <Kayit />
            </Suspense>
          )
        } />

        {/* Sifre sifirlama ve davet kabulu OTURUM ACIKKEN DE calismali:
            kullanici e-postadaki baglantiya baska bir hesapla giris yapmis
            haldeyken tiklayabilir; /dashboard'a yonlendirirsek baglanti
            sessizce yutulmus olur. */}
        <Route path="/sifre-sifirla" element={
          <Suspense fallback={<LoadingSpinner />}>
            <SifreSifirla />
          </Suspense>
        } />

        <Route path="/davet/kabul" element={
          <Suspense fallback={<LoadingSpinner />}>
            <DavetKabul />
          </Suspense>
        } />


        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Dashboard />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Sayfa yükleniyor...</p>
                  </div>
                </div>
              }>
                <Dashboard />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/customers" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Sayfa yükleniyor...</p>
                  </div>
                </div>
              }>
                <Customers />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/customers/new" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <CustomerNew />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/customers/:id" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <CustomerDetail />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/elevators" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Elevators />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/elevators/new" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <ElevatorNew />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/elevators/:id" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <ElevatorDetail />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/work-orders" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <WorkOrders />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/work-orders/new" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <WorkOrderNew />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/work-orders/:id" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <WorkOrderDetail />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/work-orders/:id/view" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <WorkOrderView />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/inventory/new" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <InventoryNew />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/inventory/:id" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <InventoryDetail />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/invoices" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Invoices />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/invoices/:id" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <InvoiceDetail />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/reports" element={<Navigate to="/dashboard" replace />} />
        
        <Route path="/users" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Users />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/permissions" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Permissions />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/uyum" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Uyum />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/bakim-operasyon" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <BakimOperasyon />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/ticari" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Ticari />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/kurulum-aktarimi" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <KurulumAktarimi />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/parametreler" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Parametreler />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/saha" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <SahaEkrani />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/kvkk" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <KvkkYonetim />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />

        {/* --- Birlestirilmis ekranlar --- */}
        {[
          { yol: '/montaj', Bilesen: Montaj },
          { yol: '/uyarilar', Bilesen: Uyarilar },
          { yol: '/entegrasyonlar', Bilesen: Entegrasyonlar },
          { yol: '/envanter', Bilesen: Envanter },
          { yol: '/yapay-zeka', Bilesen: YapayZeka },
        ].map(({ yol, Bilesen }) => (
          <Route key={yol} path={yol} element={(
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <Bilesen />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          )}
          />
        ))}

        {/* Eski adresler dogru sekmeye yonlendiriliyor: yer imleri ve
            paylasilmis baglantilar kirilmasin. */}
        {[
          ['/installation', '/montaj?sekme=projeler'],
          ['/montaj-teslim', '/montaj?sekme=teslim'],
          ['/hatirlaticilar', '/uyarilar?sekme=hatirlatici'],
          ['/smart-notifications', '/uyarilar?sekme=bildirim'],
          ['/ai/alerts', '/uyarilar?sekme=ai'],
          ['/api-settings', '/entegrasyonlar?sekme=api'],
          ['/iot-settings', '/entegrasyonlar?sekme=iot'],
          ['/inventory', '/envanter?sekme=stok'],
          ['/price-list', '/envanter?sekme=fiyat'],
          ['/ai', '/yapay-zeka?sekme=panel'],
          ['/ai/analysis', '/yapay-zeka?sekme=analiz'],
          ['/technical-assistant', '/yapay-zeka?sekme=asistan'],
          ['/contract-management', '/ticari?sekme=teklif'],
        ].map(([eski, yeni]) => (
          <Route key={eski} path={eski} element={<Navigate to={yeni} replace />} />
        ))}


        <Route path="/subscription" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Subscription />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/integrity-chain" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <IntegrityChain />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/profile" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Profile />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/chat" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Chat />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/qr-scanner" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <QRScanner />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/maintenance-tasks" element={<Navigate to="/dashboard" replace />} />

        <Route path="/price-quote" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <ElevatorPriceQuote />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/error-logs" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <ErrorLogs />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/user-settings" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <UserSettings />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/notification-test" element={<Navigate to="/dashboard" replace />} />
        
        <Route path="/notifications" element={<Navigate to="/dashboard" replace />} />
        
        <Route path="/elevator-inspections" element={<Navigate to="/elevators" replace />} />

        <Route path="/domain-operations" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <DomainOperations />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/installation-planning/:id" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <InstallationManagement />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/personnel-management" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <PersonnelManagement />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        
                <Route path="/supervisor-planning" element={
                  <ProtectedRoute>
                    <Layout>
                      <Suspense fallback={<LoadingSpinner />}>
                        <SupervisorPlanning />
                      </Suspense>
                    </Layout>
                  </ProtectedRoute>
                } />
        
        {/* AI Routes */}
        
        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      
      {/* PWA Components */}
      <PWAInstallPrompt />
      
      {/* Performance Dashboard (Development Only) */}
      <PerformanceDashboard />
      
      {/* Accessibility Toolbar */}
      <AccessibilityToolbar />
      </div>
    </OfflineProvider>
  );
}

export default App; 